import {
  DARK_PALETTE,
  LIGHT_PALETTE,
  paletteCssVariables,
  scaleCssVariables,
} from '../tokens/index';
import type { ResolvedTheme } from './theme-mode';

/**
 * Apply the token layer to a document element.
 *
 * The theme mechanism is a set of CSS custom properties on one root element, so
 * switching theme is one attribute write and every consumer reads the variables.
 * No component holds a colour literal.
 *
 * `data-fe1w0-theme` is also set, so a stylesheet can branch on the active theme
 * without duplicating the token values.
 */

export const THEME_ROOT_ATTRIBUTE = 'data-fe1w0-theme';

/** The full variable set for a theme. Pure; writes nothing. */
export function themeCssVariables(theme: ResolvedTheme): Readonly<Record<string, string>> {
  const palette = theme === 'DARK' ? DARK_PALETTE : LIGHT_PALETTE;
  return Object.freeze({ ...scaleCssVariables(), ...paletteCssVariables(palette) });
}

/**
 * Write the theme variables onto `root`.
 *
 * Every property is set with `setProperty` under the `--` namespace, so this
 * cannot clobber a structural style, and nothing outside the custom-property
 * namespace is touched.
 */
export function applyTheme(root: HTMLElement, theme: ResolvedTheme): void {
  const variables = themeCssVariables(theme);
  for (const [name, value] of Object.entries(variables)) {
    root.style.setProperty(name, value);
  }
  root.setAttribute(THEME_ROOT_ATTRIBUTE, theme);
}
